import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { AdminLayout } from '../AdminLayout';
import { formatDateInBusinessTZ, formatTimeInBusinessTZ } from '../../utils/date';
import { getUpcomingBirthdays, BirthdayStudent } from '../../utils/birthdays';
import { BirthdayBanner } from '../../components/BirthdayBanner';
import { BRAND_KEYS, type BrandKey } from '../../config/brands';
import { BRAND_PROFILES } from '../../config/brandProfiles';
import { AdminBrandTag } from '../components/AdminBrandTag';
import { enrollmentHref, paymentHref } from '../utils/recordLinks';

interface RecentEnrollment {
  id: string;
  student_name: string;
  program_name: string;
  parent_name: string;
  created_at: string;
  brand_key?: string | null;
}

interface RecentPayment {
  id: string;
  amount: number;
  student_name: string;
  program_name: string;
  payment_method: string;
  payment_method_last4?: string | null;
  status: string;
  created_at: string;
  brand_key?: string | null;
}

/** One brand's share of the brand-scoped headline figures (`byBrand` in the RPC). */
interface BrandMetrics {
  activePrograms: number;
  totalEnrollments: number;
  totalRevenue: number;
}

interface DashboardMetrics {
  totalStudents: number;
  studentsWithEnrollment: number;
  totalParents: number;
  loggedInParents: number;
  // 24h deltas
  studentsDelta24h: number;
  loggedInParentsDelta24h: number;
  parentsDelta24h: number;
  studentsWithEnrollmentDelta24h: number;
  activePrograms: number;
  programsWithEnrollment: number;
  programsDelta24h: number;
  programsWithEnrollmentDelta24h: number;
  totalEnrollments: number;
  enrollmentsWithPaidInvoice: number;
  enrollmentsDelta24h: number;
  enrollmentsWithPaymentDelta24h: number;
  totalRevenue: number;
  completedPaymentsAmount: number;
  allPaymentsDelta24h: number;
  totalRevenueDelta24h: number;
  pendingPayments: number;
  overdueInvoices: number;
  recentEnrollments: RecentEnrollment[];
  recentPayments: RecentPayment[];
  /**
   * Per-brand split of Active Programs, Total Enrollments and Total Revenue. The headline
   * figures stay the all-brand totals; this is what says so. Undefined when the database
   * function predates the split, and a brand with no programs is absent.
   */
  byBrand?: Partial<Record<BrandKey, BrandMetrics>>;
}

/**
 * "BHCS 40 · Kaleido 8" under a figure that spans every brand, so a merged number never reads
 * as one brand's. Lists every brand, with 0 for one that has nothing yet. Renders nothing when
 * the split is unavailable, which leaves the tile exactly as it was rather than showing zeros.
 */
function BrandSplit({
  byBrand,
  metric,
  format = String,
}: {
  byBrand?: Partial<Record<BrandKey, BrandMetrics>>;
  metric: keyof BrandMetrics;
  format?: (value: number) => string;
}) {
  if (!byBrand) return null;
  return (
    <dd className="text-xs text-gray-500" data-testid={`brand-split-${metric}`}>
      {BRAND_KEYS.map((key) => `${BRAND_PROFILES[key].shortName} ${format(Number(byBrand[key]?.[metric]) || 0)}`).join(' · ')}
    </dd>
  );
}

function DeltaBadge({ value, isCurrency = false }: { value: number; isCurrency?: boolean }) {
  if (value === 0) return null;
  const colorClass = value > 0 ? 'text-green-600' : 'text-red-600';
  const sign = value > 0 ? '+' : '-';
  const formatted = isCurrency
    ? `${sign}$${Math.abs(value).toFixed(2)}`
    : `${sign}${Math.abs(value)}`;
  return <span className={`ml-2 text-sm ${colorClass}`}>{formatted}</span>;
}

export function AdminOverview() {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [birthdayStudents, setBirthdayStudents] = useState<BirthdayStudent[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardMetrics();
    fetchBirthdays();
  }, []);

  async function fetchBirthdays() {
    try {
      const { data, error: birthdayError } = await supabase
        .from('students')
        .select('id, first_name, last_name, date_of_birth')
        .eq('active', true)
        .not('date_of_birth', 'is', null);

      if (birthdayError) throw birthdayError;

      setBirthdayStudents(getUpcomingBirthdays(data ?? []));
    } catch (err) {
      // Birthday banner is non-critical; log and continue.
      console.error('Error fetching birthdays:', err);
    }
  }

  async function fetchDashboardMetrics() {
    try {
      setLoading(true);
      
      // Use single RPC call to get all metrics computed server-side
      const { data, error: rpcError } = await supabase.rpc('get_admin_overview_metrics');
      
      if (rpcError) throw rpcError;
      if (!data) throw new Error('No data returned from metrics function');

      // The RPC returns a JSONB object with all metrics already computed
      const metricsData = data as {
        totalStudents: number;
        studentsWithEnrollment: number;
        totalParents: number;
        loggedInParents: number;
        studentsDelta24h: number;
        loggedInParentsDelta24h: number;
        parentsDelta24h: number;
        studentsWithEnrollmentDelta24h: number;
        activePrograms: number;
        programsWithEnrollment: number;
        programsDelta24h: number;
        programsWithEnrollmentDelta24h: number;
        totalEnrollments: number;
        enrollmentsWithPaidInvoice: number;
        enrollmentsDelta24h: number;
        enrollmentsWithPaymentDelta24h: number;
        totalRevenue: number;
        completedPaymentsAmount: number;
        allPaymentsDelta24h: number;
        totalRevenueDelta24h: number;
        pendingPayments: number;
        overdueInvoices: number;
        recentEnrollments: RecentEnrollment[];
        recentPayments: RecentPayment[];
        byBrand?: Partial<Record<BrandKey, BrandMetrics>>;
      };

      setMetrics({
        totalStudents: Number(metricsData.totalStudents) || 0,
        studentsWithEnrollment: Number(metricsData.studentsWithEnrollment) || 0,
        totalParents: Number(metricsData.totalParents) || 0,
        loggedInParents: Number(metricsData.loggedInParents) || 0,
        studentsDelta24h: Number(metricsData.studentsDelta24h) || 0,
        loggedInParentsDelta24h: Number(metricsData.loggedInParentsDelta24h) || 0,
        parentsDelta24h: Number(metricsData.parentsDelta24h) || 0,
        studentsWithEnrollmentDelta24h: Number(metricsData.studentsWithEnrollmentDelta24h) || 0,
        activePrograms: Number(metricsData.activePrograms) || 0,
        programsWithEnrollment: Number(metricsData.programsWithEnrollment) || 0,
        programsDelta24h: Number(metricsData.programsDelta24h) || 0,
        programsWithEnrollmentDelta24h: Number(metricsData.programsWithEnrollmentDelta24h) || 0,
        totalEnrollments: Number(metricsData.totalEnrollments) || 0,
        enrollmentsWithPaidInvoice: Number(metricsData.enrollmentsWithPaidInvoice) || 0,
        enrollmentsDelta24h: Number(metricsData.enrollmentsDelta24h) || 0,
        enrollmentsWithPaymentDelta24h: Number(metricsData.enrollmentsWithPaymentDelta24h) || 0,
        totalRevenue: Number(metricsData.totalRevenue) || 0,
        completedPaymentsAmount: Number(metricsData.completedPaymentsAmount) || 0,
        allPaymentsDelta24h: Number(metricsData.allPaymentsDelta24h) || 0,
        totalRevenueDelta24h: Number(metricsData.totalRevenueDelta24h) || 0,
        pendingPayments: Number(metricsData.pendingPayments) || 0,
        overdueInvoices: Number(metricsData.overdueInvoices) || 0,
        recentEnrollments: (metricsData.recentEnrollments || []).map(e => ({
          id: e.id,
          student_name: e.student_name,
          program_name: e.program_name,
          parent_name: e.parent_name,
          created_at: e.created_at,
          brand_key: e.brand_key,
        })),
        recentPayments: (metricsData.recentPayments || []).map(p => ({
          id: p.id,
          amount: Number(p.amount) || 0,
          student_name: p.student_name,
          program_name: p.program_name,
          payment_method: p.payment_method,
          payment_method_last4: p.payment_method_last4,
          status: p.status,
          created_at: p.created_at,
          brand_key: p.brand_key,
        })),
        byBrand: metricsData.byBrand,
      });

      setLoading(false);
    } catch (error: unknown) {
      console.error('Error fetching dashboard metrics:', error);
      const errorObj = error as { message?: string };
      setError(errorObj.message || 'An unknown error occurred');
      setLoading(false);
    }
  }

  return (
    <AdminLayout>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <svg className="animate-spin h-4 w-4 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
          <span className="text-gray-600">Loading dashboard metrics...</span>
        </div>
      ) : error ? (
        <div className="mb-4 p-4 bg-red-50 text-red-600 rounded">
          {error}
        </div>
      ) : !metrics ? (
        <div className="mb-4 p-4 bg-yellow-50 text-yellow-600 rounded">
          No metrics data available.
        </div>
      ) : (
      <div className="space-y-6 flex-1 min-h-0 overflow-y-auto">
        <BirthdayBanner
          students={birthdayStudents}
          onClick={() => navigate('/admin-app/students')}
        />

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
          <div 
            className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200"
            onClick={() => navigate('/admin-app/students')}
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Students</dt>
                    <dd className="text-lg font-medium text-gray-900 flex items-baseline">{metrics.totalStudents}<DeltaBadge value={metrics.studentsDelta24h} /></dd>
                    <dd className="text-sm text-gray-600 flex items-baseline">{metrics.studentsWithEnrollment} with enrollment<DeltaBadge value={metrics.studentsWithEnrollmentDelta24h} /></dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div 
            className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200"
            onClick={() => navigate('/admin-app/parents')}
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Parents</dt>
                    <dd className="text-lg font-medium text-gray-900 flex items-baseline">{metrics.totalParents}<DeltaBadge value={metrics.parentsDelta24h} /></dd>
                    <dd className="text-sm text-gray-600 flex items-baseline">{metrics.loggedInParents} have logged in<DeltaBadge value={metrics.loggedInParentsDelta24h} /></dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div 
            className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200"
            onClick={() => navigate('/admin-app/programs')}
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Active Programs</dt>
                    <dd className="text-lg font-medium text-gray-900 flex items-baseline">{metrics.activePrograms}<DeltaBadge value={metrics.programsDelta24h} /></dd>
                    <dd className="text-sm text-gray-600 flex items-baseline">{metrics.programsWithEnrollment} with enrollment<DeltaBadge value={metrics.programsWithEnrollmentDelta24h} /></dd>
                    <BrandSplit byBrand={metrics.byBrand} metric="activePrograms" />
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div 
            className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200"
            onClick={() => navigate('/admin-app/finances')}
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Revenue</dt>
                    <dd className="text-lg font-medium text-gray-900 flex items-baseline">${metrics.totalRevenue.toFixed(2)}<DeltaBadge value={metrics.totalRevenueDelta24h} isCurrency /></dd>
                    <dd className="text-sm text-gray-600 flex items-baseline">${metrics.completedPaymentsAmount.toFixed(2)} all payments<DeltaBadge value={metrics.allPaymentsDelta24h} isCurrency /></dd>
                    <BrandSplit byBrand={metrics.byBrand} metric="totalRevenue" format={(n) => `$${n.toFixed(2)}`} />
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div 
            className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200"
            onClick={() => navigate('/admin-app/enrollments')}
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Enrollments</dt>
                    <dd className="text-lg font-medium text-gray-900 flex items-baseline">{metrics.totalEnrollments}<DeltaBadge value={metrics.enrollmentsDelta24h} /></dd>
                    <dd className="text-sm text-gray-600 flex items-baseline">{metrics.enrollmentsWithPaidInvoice} with payment<DeltaBadge value={metrics.enrollmentsWithPaymentDelta24h} /></dd>
                    <BrandSplit byBrand={metrics.byBrand} metric="totalEnrollments" />
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Enrollments</h3>
            <div className="space-y-3">
              {metrics.recentEnrollments.map((enrollment) => {
                const timeString = formatTimeInBusinessTZ(enrollment.created_at);
                
                // A link, not a click handler, so ⌘-click opens the row in a new tab.
                return (
                  <Link
                    key={enrollment.id}
                    to={enrollmentHref(enrollment.id)}
                    className="flex items-center justify-between py-3 -mx-2 px-2 rounded-md border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1 pr-6">
                      <div className="text-sm font-medium text-gray-900">{enrollment.student_name}</div>
                      <div className="text-sm text-gray-500">{enrollment.program_name} <AdminBrandTag brand={enrollment.brand_key} /></div>
                      <div className="text-xs text-gray-400 mt-1">
                        by {enrollment.parent_name}
                      </div>
                    </div>
                    <div className="flex flex-col items-end justify-center">
                      <div className="text-sm text-gray-500">
                        {formatDateInBusinessTZ(enrollment.created_at)}
                      </div>
                      <div className="text-xs text-gray-400">
                        {timeString}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Payments</h3>
            <div className="space-y-3">
              {metrics.recentPayments.map((payment) => {
                // Format payment method for display
                const getPaymentMethodDisplay = (method: string, last4?: string | null) => {
                  switch (method?.toLowerCase()) {
                    case 'card':
                      return 'Credit Card';
                    case 'ach':
                      return last4 ? `Bank Account •••• ${last4}` : 'Bank Account';
                    case 'zelle':
                      return 'Zelle';
                    case 'cash':
                      return 'Cash';
                    case 'check':
                      return 'Check';
                    default:
                      return method || 'Unknown';
                  }
                };

                const timeString = formatTimeInBusinessTZ(payment.created_at);

                return (
                  <Link
                    key={payment.id}
                    to={paymentHref(payment.id)}
                    className="flex items-center justify-between py-2 -mx-2 px-2 rounded-md border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1 pr-4">
                      <div className="text-sm font-medium text-gray-900">{payment.student_name}</div>
                      <div className="text-sm text-gray-500">{payment.program_name} <AdminBrandTag brand={payment.brand_key} /></div>
                      <div className="text-xs text-gray-400 mt-1">
                        {getPaymentMethodDisplay(payment.payment_method, payment.payment_method_last4)}
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <div className={`px-2 py-1 rounded text-sm font-medium ${
                        payment.status === 'completed' ? 'bg-green-100 text-green-800' : 
                        payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                        payment.status === 'failed' ? 'bg-red-100 text-red-800' :
                        payment.status === 'refunded' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        ${payment.amount.toFixed(2)}
                      </div>
                      <div className="text-right mt-1">
                        <p className="text-sm text-gray-500">
                          {formatDateInBusinessTZ(payment.created_at)}
                        </p>
                        <p className="text-xs text-gray-400">
                          {timeString}
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      )}
    </AdminLayout>
  );
} 